/**
 * UI language. The Arabic text in the code is the key (gettext style); en.json
 * maps it to English. A missing entry falls back to the Arabic, so a screen is
 * never blank — tests/i18n.test.js keeps en.json complete.
 *
 *   template: {{ $t('الموظّفون') }} · {{ $t('{n} طلب', { n }) }}
 *   script:   import { t } from '../i18n'; toast(t('تم الحفظ'))
 *
 * The language is fixed for the page's life (switching reloads), so a t() at
 * module level — a label table, a route title — is already in the right language.
 * Order: the viewer's choice on this device → the brand's default → Arabic.
 */
import brand from '../brand'
import en from './en.json'

const KEY = 'hr_lang'
export const LOCALES = [
  { code: 'ar', label: 'العربيّة', short: 'ع', dir: 'rtl' },
  { code: 'en', label: 'English', short: 'EN', dir: 'ltr' },
]

function pick() {
  try {
    const v = localStorage.getItem(KEY)
    if (LOCALES.some((l) => l.code === v)) return v
  } catch (_) { /* storage blocked → brand default */ }
  return brand.locale === 'en' ? 'en' : 'ar'
}

export const locale = pick()
export const isEn = locale === 'en'
export const dir = isEn ? 'ltr' : 'rtl'
/** For Intl / toLocale*(): English UI → en-GB (day first), Arabic UI → ar. */
export const intlLocale = isEn ? 'en-GB' : 'ar'

const dict = isEn ? en : null

/**
 * Translate. `{name}` placeholders are filled from `params`; an English entry
 * "one|many" picks the singular when its first numeric placeholder is 1.
 */
export function t(text, params) {
  let out = (dict && dict[text]) || text
  if (params) {
    if (dict && out.includes('|')) {
      // The count is the first numeric placeholder of the singular form.
      const [one, many] = out.split('|')
      const k = (one.match(/\{\w+\}/g) || []).map((m) => m.slice(1, -1))
        .find((x) => params[x] !== '' && params[x] != null && !Number.isNaN(Number(params[x])))
      out = k && Number(params[k]) === 1 ? one : many
    }
    out = out.replace(/\{(\w+)\}/g, (m, k) => (params[k] ?? m))
  }
  return out
}

/**
 * A record's text in the UI language: <field>_ar / <field>_en / <field> —
 * departments carry name + name_ar, notifications title_ar + title_en.
 */
export function loc(obj, field = 'name') {
  if (!obj) return ''
  const ar = obj[`${field}_ar`], en = obj[`${field}_en`], plain = obj[field]
  return (isEn ? en || plain || ar : ar || plain || en) || ''
}

/**
 * An employee's full name. Names are one field in whatever script the company
 * typed them (migration 017); first_name_ar / last_name_ar are only the legacy
 * copy, read when the main field is empty.
 */
export function personName(p) {
  if (!p) return ''
  return [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
    || [p.first_name_ar, p.last_name_ar].filter(Boolean).join(' ').trim()
}

export function setLocale(code) {
  if (code === locale) return
  try { localStorage.setItem(KEY, code) } catch (_) { /* nothing to persist into */ }
  location.reload()
}

/** <html lang dir> before the first paint of the app. */
export function applyDocumentLocale() {
  document.documentElement.lang = locale
  document.documentElement.dir = dir
}

export default {
  install(app) {
    app.config.globalProperties.$t = t
    app.config.globalProperties.$loc = loc
    app.config.globalProperties.$person = personName
    app.config.globalProperties.$isEn = isEn
  },
}
